import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const CapTableDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Mock Data for Cap Table --
    const mockCapTable = [
        { key: '1', entity: 'Reliance Industries Ltd', shareholder: 'Ambani Family', holding: '50%', type: 'Promoter', notes: '—' },
        { key: '2', entity: 'Reliance Industries Ltd', shareholder: 'Institutional Investors', holding: '40%', type: 'Institution', notes: '—' },
        { key: '3', entity: 'Jio Platforms', shareholder: 'Strategic Partners', holding: '10%', type: 'Partner', notes: '—' },
    ];

    const filteredData = mockCapTable.filter(item => {
        const matchesSearch = item.entity.toLowerCase().includes(searchText.toLowerCase()) ||
            item.shareholder.toLowerCase().includes(searchText.toLowerCase());
        const matchesType = typeFilter === 'All Types' || item.type === typeFilter;
        return matchesSearch && matchesType;
    });

    const renderTypeTag = (type: string) => {
        let color = 'default';
        if (type === 'Promoter') color = 'blue';
        if (type === 'Institution') color = 'green';
        if (type === 'Partner') color = 'magenta';

        return <Tag color={color} style={{ minWidth: '80px', textAlign: 'center' }}>{type}</Tag>;
    };

    // Derived Metrics
    const totalEntities = new Set(filteredData.map(d => d.entity)).size;
    const totalShareholders = filteredData.length;
    // Calculate avg holding (parse string '50%' -> 50)
    const avgHolding = (filteredData.reduce((acc, curr) => acc + parseFloat(curr.holding), 0) / (filteredData.length || 1)).toFixed(1);

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        PASSIONIT — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Cap</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Cap Table & Metrics */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Cap Table & Exposure</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search entity or shareholder..."
                                    style={{ width: 240 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Types" onChange={setTypeFilter} style={{ width: 140 }}>
                                    <Option value="All Types">All Types</Option>
                                    <Option value="Promoter">Promoter</Option>
                                    <Option value="Institution">Institution</Option>
                                    <Option value="Partner">Partner</Option>
                                </Select>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredData.length} of {mockCapTable.length} entries</Text>

                        <Table
                            dataSource={filteredData}
                            pagination={false}
                            columns={[
                                { title: 'ENTITY', dataIndex: 'entity', key: 'entity', render: (text) => <b>{text}</b> },
                                { title: 'SHAREHOLDER', dataIndex: 'shareholder', key: 'shareholder', render: (text) => <Text>{text}</Text> },
                                { title: 'HOLDING %', dataIndex: 'holding', key: 'holding', render: (text) => <b>{text}</b> },
                                { title: 'TYPE', dataIndex: 'type', key: 'type', align: 'center', render: (type) => renderTypeTag(type) },
                                { title: 'NOTES', dataIndex: 'notes', key: 'notes', align: 'center', render: (text) => <Text type="secondary">{text}</Text> }
                            ]}
                        />

                        {/* Summary Metrics */}
                        <Row gutter={16} className="mt-4">
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Total Entities</Text>
                                    <Title level={3} className="text-primary m-0">{totalEntities}</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">Total Shareholders</Text>
                                    <Title level={3} className="text-success m-0">{totalShareholders}</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f9f0ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Avg Holding</Text>
                                    <Title level={3} style={{ color: '#722ed1', margin: 0 }}>{avgHolding}%</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default CapTableDashboard;
